import { useEffect, useState } from "react";
import axios from "axios";
import { useReveal } from "../hooks/useReveal";
import { STREAMING } from "../lib/links";
import { Play } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LatestRelease() {
  const [release, setRelease] = useState(null);
  const { ref, visible } = useReveal();

  useEffect(() => {
    axios.get(`${API}/release/latest`).then((res) => setRelease(res.data)).catch(() => setRelease(null));
  }, []);

  const songUrl = release?.listen_url || STREAMING.spotify;
  const songTitle = release?.title || "Only You";
  const songType = release?.type || "Single";
  const songYear = release?.release_date || "2025";
  const artwork = release?.artwork_url;

  return (
    <section
      id="music"
      data-testid="latest-release-section"
      ref={ref}
      className={`relative bg-[#FAFBFC] py-24 md:py-32 px-6 md:px-12 lg:px-20 reveal ${
        visible ? "in-view" : ""
      }`}
    >
      <div className="max-w-[1100px] mx-auto">
        <div className="flex items-center gap-4 mb-12">
          <div className="overline" data-testid="release-overline">music · out now</div>
          <div className="h-px flex-1 bg-[#E6EAEF]" />
          <div className="font-mono-meta text-[10px] tracking-[0.3em] uppercase text-[#8C8C8C]">
            {songType} · {songYear}
          </div>
        </div>

        <a
          href={songUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="song-row"
          className="group flex items-center gap-5 md:gap-7 p-4 md:p-5 border border-[#E6EAEF] hover:border-[#6B8FAE] bg-white transition-all duration-400"
        >
          <div className="hidden sm:flex w-6 items-center justify-center font-mono-meta text-[11px] text-[#8C8C8C] group-hover:text-[#6B8FAE] transition-colors">
            <span className="group-hover:hidden">01</span>
            <Play className="hidden group-hover:block w-4 h-4" strokeWidth={1.5} />
          </div>

          <div
            data-testid="album-artwork"
            className="relative w-16 h-16 md:w-20 md:h-20 shrink-0 overflow-hidden border border-[#E6EAEF]"
          >
            {artwork && (
              <img
                src={artwork}
                alt={`${songTitle} cover`}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3
              data-testid="song-title"
              className="font-serif-display text-2xl md:text-3xl text-[#0A0A0A] leading-tight truncate"
            >
              {songTitle}
            </h3>
            <div className="mt-1 font-mono-meta text-[10px] tracking-[0.28em] uppercase text-[#8C8C8C]">
              Sylas Ever  ·  {songType}
            </div>
          </div>

          <div className="hidden md:flex items-center gap-2">
            {[
              { name: "Spotify", url: STREAMING.spotify },
              { name: "Apple Music", url: STREAMING.appleMusic },
              { name: "YouTube", url: STREAMING.youtube },
            ].map((s) => (
              <span
                key={s.name}
                data-testid={`stream-${s.name.toLowerCase().replace(/\s/g, "-")}`}
                className="font-mono-meta text-[9px] tracking-[0.28em] uppercase text-[#595959] border border-[#E6EAEF] px-3 py-1.5"
              >
                {s.name}
              </span>
            ))}
          </div>

          <div className="font-mono-meta text-[10px] tracking-[0.32em] uppercase text-[#595959] group-hover:text-[#6B8FAE] flex items-center gap-2 shrink-0 transition-colors">
            <span className="hidden lg:inline">listen</span>
            <span className="inline-block transition-transform duration-500 group-hover:translate-x-1">→</span>
          </div>
        </a>

        {/* Mobile streaming chips */}
        <div className="md:hidden mt-5 flex flex-wrap gap-2">
          {[
            { name: "Spotify", url: STREAMING.spotify },
            { name: "Apple Music", url: STREAMING.appleMusic },
            { name: "YouTube", url: STREAMING.youtube },
          ].map((s) => (
            <a
              key={s.name}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`mobile-stream-${s.name.toLowerCase().replace(/\s/g, "-")}`}
              className="font-mono-meta text-[10px] tracking-[0.28em] uppercase text-[#0A0A0A] border border-[#E6EAEF] px-3 py-1.5"
            >
              {s.name}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
